import { memo } from "react";
import { Dropdown } from "react-bootstrap";
import styled from "styled-components";
import ReactCountryFlag from "react-country-flag";
import {
  DarkDropdownMenu,
  DarkDropdownItem,
} from "components/shared/DarkDropdownStyles";
import { useAppDispatch, useAppSelector } from "hooks";
import { languageSelector } from "selectors/uiSelectors";
import uiSlice, { Language } from "reducers/uiSlice";
import { useNavigate, useLocation } from "react-router-dom";
import { localizePath, stripLangPrefix } from "utility/languageUtility";

const LanguageDropdown = styled(Dropdown)`
  @media only screen and (max-width: 699px) {
    width: 100%;
  }

  .dropdown-toggle {
    background: transparent;
    border: none;
    color: white;
    padding: 5px 10px;
    display: flex;
    align-items: center;
    gap: 6px;

    &:hover,
    &:focus,
    &:active {
      background: transparent !important;
      border: none !important;
      box-shadow: none !important;
      color: white !important;
    }

    &::after {
      margin-left: 6px;
    }

    @media only screen and (max-width: 699px) {
      width: 100%;
      justify-content: flex-start;
    }
  }

  .dropdown-menu {
    @media only screen and (max-width: 699px) {
      left: 0 !important;
      right: auto !important;
    }
  }
`;

const FlagIcon = styled.span`
  display: inline-flex;
  align-items: center;

  svg {
    width: 1.2em;
    height: 1.2em;
  }
`;

const LanguageCode = styled.span`
  color: white;
  font-size: 0.9rem;
  font-weight: 500;
`;

const languages: Record<Language, { countryCode: string; code: string }> = {
  en: { countryCode: "GB", code: "EN" },
  es: { countryCode: "ES", code: "ES" },
  "zh-CN": { countryCode: "CN", code: "中文" },
};

const LanguageSelector = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const language = useAppSelector(languageSelector);

  const handleSelect = (lang: Language) => {
    if (lang === language) return;
    dispatch(uiSlice.actions.setLanguage(lang));
    // Domain pages have no es equivalent — stay on the same page (English URL)
    const isDomainPage = location.pathname.startsWith("/domain/");
    if (isDomainPage) return;
    const basePath = stripLangPrefix(location.pathname);
    navigate(localizePath(basePath, lang));
  };

  return (
    <LanguageDropdown>
      <Dropdown.Toggle id="language-dropdown">
        <FlagIcon>
          <ReactCountryFlag
            countryCode={languages[language].countryCode}
            svg
            style={{ width: "1.2em", height: "1.2em" }}
          />
        </FlagIcon>
        <LanguageCode>{languages[language].code}</LanguageCode>
      </Dropdown.Toggle>
      <DarkDropdownMenu align="end" style={{ minWidth: "100px" }}>
        <DarkDropdownItem
          onClick={() => handleSelect("en")}
          active={language === "en"}
          style={{ display: "flex", alignItems: "center", gap: "8px" }}
        >
          <FlagIcon>
            <ReactCountryFlag
              countryCode="GB"
              svg
              style={{ width: "1.2em", height: "1.2em" }}
            />
          </FlagIcon>{" "}
          EN
        </DarkDropdownItem>
        <DarkDropdownItem
          onClick={() => handleSelect("es")}
          active={language === "es"}
          style={{ display: "flex", alignItems: "center", gap: "8px" }}
        >
          <FlagIcon>
            <ReactCountryFlag
              countryCode="ES"
              svg
              style={{ width: "1.2em", height: "1.2em" }}
            />
          </FlagIcon>{" "}
          ES
        </DarkDropdownItem>
        <DarkDropdownItem
          onClick={() => handleSelect("zh-CN")}
          active={language === "zh-CN"}
          style={{ display: "flex", alignItems: "center", gap: "8px" }}
        >
          <FlagIcon>
            <ReactCountryFlag
              countryCode="CN"
              svg
              style={{ width: "1.2em", height: "1.2em" }}
            />
          </FlagIcon>{" "}
          中文
        </DarkDropdownItem>
      </DarkDropdownMenu>
    </LanguageDropdown>
  );
};

export default memo(LanguageSelector);
